import { useQuery } from "@apollo/client/react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import { ReportsViewMetricWidget } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewMetricWidget";
import { ReportsViewWidgetPairingPicker } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidgetPairingPicker";
import { buildWidgetFilterSet } from "@/app/app/_/views/ReportsView/reportFilters";
import { REPORT_WIDGET_CONTENT_QUERY } from "@/app/app/_/views/ReportsView/reportWidgetQueries";
import { useReportsViewWidgetEffectiveFilters } from "@/app/app/_/views/ReportsView/ReportsViewWidgetHeaderFilters.hooks";
import type { ReportWidgetShapeFragment } from "@/__generated__/graphql";
import type { ReportLayoutWidget } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  defaultQuestionFor,
  specForQuestion,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  getWidgetPairingStatus,
  normalizeWidget,
  presentationsForSource,
  sourceByName,
  sourcesForPresentation,
  type PresentationKind,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { useReportSourceCatalog } from "@/app/app/_/views/ReportsView/reportFilterDefinitions.hooks";
import { ReportWidgetBody } from "@/components/organisms/ReportWidget/ReportWidgetBody";
import { objectForReportSource } from "@/objects";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import { formatApolloError } from "@/utils/graphqlErrors";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

type ReportsViewWidgetProps = {
  widget: ReportLayoutWidget;
  reportIdentifier: string | null;
  layoutEntityIdentifiers: string[];
  isLoading?: boolean;
  /**
   * The widget as the server has it, once it has all of it: until then there
   * is nothing to ask the server about. When it catches up with the question
   * on screen, that question is asked.
   */
  savedWidget?: ReportLayoutWidget | null;
  onCompletePairing?: (
    widgetId: string,
    patch: Partial<ReportLayoutWidget>,
  ) => void;
};

/** Routes a placed report widget to the unified report widget query. */
function ReportsViewWidgetContent({
  widget,
  reportIdentifier,
  layoutEntityIdentifiers,
  isLoading = false,
  savedWidget = null,
  onCompletePairing,
}: ReportsViewWidgetProps) {
  const globalFilters = State((state) => state.filters);
  const catalogue = useReportSourceCatalog();
  const effectiveFilters = useReportsViewWidgetEffectiveFilters(widget);

  const filterSets = useMemo(
    () =>
      buildWidgetFilterSet({
        widget,
        globalFilters,
        layoutEntityIdentifiers,
        widgetFilters: effectiveFilters,
      }),
    [widget, globalFilters, layoutEntityIdentifiers, effectiveFilters],
  );

  const nodeId = useMemo(
    () => identifierAndTypenameToId(widget.id, "ReportWidgetObject"),
    [widget.id],
  );

  const pairingStatus = getWidgetPairingStatus(widget);
  const canLoadContent = pairingStatus === "complete" && savedWidget != null;

  const { data, error, refetch } = useQuery(REPORT_WIDGET_CONTENT_QUERY, {
    variables: {
      id: nodeId,
      ...filterSets,
    },
    skip: !reportIdentifier || !widget.id || isLoading || !canLoadContent,
    fetchPolicy: "network-only",
  });

  // What this card is asking for, all of it: the question and how it is to be
  // drawn. The server shapes its answer by both - a table and a bar chart of
  // one question are different answers - so a change to either is a new thing
  // to ask.
  //
  // Written out the same way whoever wrote it: the server's copy of a question
  // names every part of it, empty ones included, and carries the cache's
  // ``__typename`` on each; the one on screen names only what was set. Said
  // with nothing empty in it and its parts in order, the two read alike when
  // they ask the same thing - otherwise the server never seemed to have the
  // question on screen, and a changed question was never asked.
  const askedOf = (asking: ReportLayoutWidget) =>
    JSON.stringify(
      { spec: asking.spec ?? {}, presentation: asking.presentation },
      (key, value: unknown) => {
        if (
          key === "__typename" ||
          value == null ||
          value === false ||
          value === "" ||
          (Array.isArray(value) && value.length === 0)
        ) {
          return undefined;
        }
        if (typeof value === "object" && !Array.isArray(value)) {
          return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
              a.localeCompare(b),
            ),
          );
        }
        return value;
      },
    );
  const asked = askedOf(widget);
  const askedRef = useRef(asked);
  const savedAsked = savedWidget ? askedOf(savedWidget) : null;

  // Asked again once the server has the question, not when the question was
  // changed: asked in between, the server answers the question it still has,
  // and the card draws the old chart under the new sentence.
  useEffect(() => {
    if (askedRef.current === asked) return;
    if (!canLoadContent || savedAsked !== asked) return;
    askedRef.current = asked;
    void refetch();
  }, [savedAsked]);

  /**
   * What is on screen: an answer, and the drawing it was an answer for.
   *
   * Drawn as the card's *current* presentation, an answer that has not caught
   * up yet is drawn the wrong way - last question's categories strung along a
   * line, a message about there being nothing to show - and the labels change
   * a moment after the chart does. Held together, the card shows the old
   * chart until the whole of the new one has arrived, and then changes once.
   */
  const [shown, setShown] = useState<{
    presentation: PresentationKind;
    content: ReportWidgetShapeFragment | null;
  } | null>(null);
  useEffect(() => {
    if (!data) return;
    const answered =
      data.node?.__typename === "ReportWidgetObject" ? data.node : null;
    setShown({
      presentation: normalizeWidget(widget).presentation as PresentationKind,
      content: (answered?.data as ReportWidgetShapeFragment) ?? null,
    });
  }, [data]);

  if (isLoading) {
    return renderWidgetSkeleton(widget);
  }

  if (pairingStatus === "needs-presentation" && widget.source) {
    const options = presentationsForSource(
      sourceByName(widget.source, catalogue),
    ).map((entry) => ({
      id: entry.kind,
      label: entry.label,
      description: entry.description,
    }));

    return (
      <ReportsViewWidgetPairingPicker
        options={options}
        onSelect={(presentation) =>
          onCompletePairing?.(widget.id, {
            presentation: presentation as PresentationKind,
          })
        }
      />
    );
  }

  if (pairingStatus === "needs-source" && widget.presentation) {
    const options = sourcesForPresentation(widget.presentation, catalogue).map(
      (source) => ({
        id: source.name,
        label: source.label,
        // What the rows are, said where they are declared - the same line the
        // catalogue this widget could have been dragged from shows.
        description:
          objectForReportSource(source.name)?.report?.description ?? "",
      }),
    );

    return (
      <ReportsViewWidgetPairingPicker
        options={options}
        onSelect={(source) => {
          const chosen = sourceByName(source, catalogue);
          onCompletePairing?.(widget.id, {
            source,
            // Given a source, a widget asks it the question its rows most
            // obviously answer - the same one a source dropped straight onto
            // the canvas arrives with - so it draws something at once.
            ...(chosen?.isSummarized
              ? {
                  spec: specForQuestion(
                    widget.spec,
                    defaultQuestionFor(chosen, widget.presentation),
                    chosen,
                  ),
                }
              : {}),
          });
        }}
      />
    );
  }

  // A skeleton only for a widget with nothing to show yet. One asked again -
  // new filters, a question the server now has - keeps its answer on screen
  // until the next one arrives: dropped for a skeleton in between, what was
  // drawn was taken down and put up again, and the figures on it counted up
  // from nothing a second time.
  if (!canLoadContent || !shown) {
    return renderWidgetSkeleton(widget);
  }

  if (error) {
    return (
      <Typography level="body-sm" className="text-destructive">
        {formatApolloError(error)}
      </Typography>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <ReportWidgetBody
        data={shown.content}
        presentation={shown.presentation}
        rows={sourceByName(widget.source, catalogue)?.label}
        isLinked
      />
    </div>
  );
}

/**
 * A widget is drawn again only when what it shows could have changed. A
 * report is normalized afresh on every edit and every save, so its widgets
 * arrive as new objects saying the same thing - and moving one card a square
 * used to draw every chart on the page again, every frame of the drag. Data is
 * compared by what it says; a callback by which one it is.
 */
export function propsSayTheSame<Props extends object>(
  previous: Props,
  next: Props,
): boolean {
  const keys = Object.keys({ ...previous, ...next }) as Array<keyof Props>;
  return keys.every(
    (key) =>
      previous[key] === next[key] ||
      (typeof next[key] === "object" &&
        JSON.stringify(previous[key]) === JSON.stringify(next[key])),
  );
}

export const ReportsViewWidget = memo(
  ReportsViewWidgetContent,
  propsSayTheSame,
);

function ReportsViewChartSkeleton() {
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      <Skeleton variant="rectangular" className="flex-1 min-h-30" />
      <div className="flex gap-2 justify-center">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="text" width={40} />
        ))}
      </div>
    </div>
  );
}

function ReportsViewListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2 flex-1">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="rectangular" height={32} />
      ))}
    </div>
  );
}

/** Grid stand-in: rows at the real grid row height, hairlines between them. */
function ReportsViewTableSkeleton() {
  return (
    <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden gap-px">
      {Array.from({ length: 12 }, (_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          className="flex-[0_0_44px] rounded-none"
        />
      ))}
    </div>
  );
}

/** The placeholder a widget holds while it loads, matched to how it is drawn. */
function renderWidgetSkeleton(widget: ReportLayoutWidget) {
  const presentation = widget.presentation
    ? normalizeWidget(widget).presentation
    : "BAR_CHART";
  switch (presentation) {
    case "KPI_STRIP":
      return (
        <div className="flex flex-col items-center justify-center flex-1 min-h-0 w-full gap-4">
          <ReportsViewMetricWidget value={0} isLoading />
          <Skeleton variant="text" width={200} />
        </div>
      );
    case "MATRIX":
      // The matrix and the grouped summary inset themselves inside a flush
      // card, so their placeholders carry the same inset or the content jumps
      // away from the card edge once it lands.
      return (
        <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden pl-3 pr-3 pt-2 pb-2">
          <ReportsViewTableSkeleton />
        </div>
      );
    case "GROUPED_SUMMARY":
      return (
        <div className="flex-1 min-h-0 w-full flex flex-col overflow-hidden pl-4 pr-4 pt-3 pb-3">
          <ReportsViewListSkeleton rows={6} />
        </div>
      );
    case "TREE":
    case "TABLE":
      return <ReportsViewTableSkeleton />;
    default:
      return <ReportsViewChartSkeleton />;
  }
}
